import { User, Briefcase, Mail, Phone, Globe, Linkedin, Twitter, Instagram, MapPin, Award } from "lucide-react";

const ProfileShowcaseSection = () => {
  return (
    <section className="py-24 bg-slate-950">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4 border border-primary/30">
            Your Digital Identity
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            What's Actually on{" "}
            <span className="text-primary">Your QCard</span>
          </h2>
          <p className="text-lg text-white/50">
            Everything they need to remember you, reach you, and do business with you.
          </p>
        </div>

        {/* Profile Mockup */}
        <div className="max-w-lg mx-auto">
          <div className="relative">
            {/* Glow */}
            <div className="absolute inset-0 bg-gradient-to-b from-primary/20 to-transparent rounded-3xl blur-2xl" />
            
            {/* Card */}
            <div className="relative bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl border border-white/10 overflow-hidden shadow-2xl">
              {/* Header */}
              <div className="h-32 bg-gradient-to-r from-primary via-qcard-coral to-primary relative">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50" />
              </div>
              
              {/* Avatar */}
              <div className="flex justify-center -mt-16 relative z-10">
                <div className="w-32 h-32 rounded-full bg-slate-800 border-4 border-slate-900 flex items-center justify-center shadow-xl">
                  <span className="text-4xl font-bold text-white">JD</span>
                </div>
              </div>
              
              {/* Profile Info */}
              <div className="px-8 pt-4 pb-8 text-center">
                <h3 className="text-2xl font-bold text-white mb-1">John Doe</h3>
                <p className="text-primary font-medium mb-1">CEO & Founder</p>
                <p className="text-white/50 text-sm mb-6">Acme Corporation</p>
                
                {/* CTA Button */}
                <button className="w-full py-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl mb-6 transition-colors shadow-lg shadow-primary/30">
                  Add to Contacts
                </button>
                
                {/* Contact Details */}
                <div className="space-y-3 text-left">
                  <div className="flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-white/40 text-xs">Phone</p>
                      <p className="text-white text-sm">+1 (555) 123-4567</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-white/40 text-xs">Email</p>
                      <p className="text-white text-sm">john@acme.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-white/40 text-xs">Website</p>
                      <p className="text-white text-sm">www.acme.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-white/40 text-xs">Location</p>
                      <p className="text-white text-sm">San Francisco, CA</p>
                    </div>
                  </div>
                </div>
                
                {/* Social Links */}
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-white/40 text-xs mb-4">Connect on Social</p>
                  <div className="flex justify-center gap-3">
                    {[
                      { icon: Linkedin, color: "bg-blue-600" },
                      { icon: Twitter, color: "bg-sky-500" },
                      { icon: Instagram, color: "bg-gradient-to-br from-purple-600 to-pink-500" },
                    ].map((social, idx) => (
                      <div
                        key={idx}
                        className={`w-12 h-12 rounded-xl ${social.color} flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity`}
                      >
                        <social.icon className="w-5 h-5 text-white" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features List */}
        <div className="max-w-3xl mx-auto mt-16">
          <div className="grid sm:grid-cols-3 gap-6 text-center">
            {[
              { icon: User, label: "Your Photo", desc: "Face recognition matters" },
              { icon: Briefcase, label: "Full Credentials", desc: "Title, company, bio" },
              { icon: Award, label: "Social Proof", desc: "Links & testimonials" },
            ].map((feature, idx) => (
              <div key={idx} className="p-6 rounded-2xl bg-white/5 border border-white/10">
                <feature.icon className="w-8 h-8 text-primary mx-auto mb-3" />
                <p className="text-white font-semibold mb-1">{feature.label}</p>
                <p className="text-white/50 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfileShowcaseSection;
